import { useRef } from 'react';
import { isWeb } from '@helpers';
import type { ManageTextInputOverlayResult, TextAnnotation, TextEditingRequest, Viewport } from '@v2/components/imageHandling/components/DrawingCanvas/types';
import type { ImageViewTransform } from '@v2/components/imageHandling/components/DrawingCanvas/helpers/imageViewTransformStore';

export interface UseManageTextInputOverlayParams {
  editing: TextEditingRequest | null;
  annotation: TextAnnotation | null;
  containerRect: DOMRect | null;
  svgCtm: DOMMatrix | null;
  /** Native fallback — used to reapply the same zoom/pan the SVG layer uses on `annotation.position`. */
  viewport: Viewport;
  viewTransform: ImageViewTransform;
  onCommit: () => void;
  onCancel: () => void;
}

/**
 * Resolves the floating <input>'s screen position and gates blur-commit until the input has
 * actually been focused (avoids the auto-focus race where blur fires before focus, prematurely
 * committing the empty draft).
 */
export const useManageTextInputOverlay = ({
  editing,
  annotation,
  containerRect,
  svgCtm,
  viewport,
  viewTransform,
  onCommit,
  onCancel,
}: UseManageTextInputOverlayParams): ManageTextInputOverlayResult => {
  const hasFocusedRef = useRef(false);

  let left = 0;
  let top = 0;
  let fontSize = annotation?.fontSize ?? 0;

  if (annotation && editing) {
    // SVG text positions `y` at the alphabetic baseline; visible glyph top sits at
    // `baseline - ASCENT_RATIO * fontSize`. The TextInput is rendered with a tight line box
    // (lineHeight === height === fontSize, padding 0, textAlignVertical: 'top'), so the input's
    // visible text top equals `inputTop` — no extra inner padding to compensate for.
    const ASCENT_RATIO = 0.8;
    if (isWeb && svgCtm && containerRect) {
      const screenX = svgCtm.a * annotation.position.x + svgCtm.e;
      const screenY = svgCtm.d * annotation.position.y + svgCtm.f;
      left = screenX - containerRect.left;
      fontSize = annotation.fontSize * svgCtm.d;
      top = screenY - containerRect.top - fontSize * ASCENT_RATIO;
    } else {
      // Native: reapply the same zoom/pan the SVG wrapper uses on `annotation.position`. This
      // anchors the input to the annotation's saved location (existing edits stay in place)
      // instead of jumping to wherever the user tapped.
      const z = viewTransform.zoom || 1;
      const cx = viewport.viewportWidth / 2;
      const cy = viewport.viewportHeight / 2;
      left = (annotation.position.x - cx) * z + cx + viewTransform.panX;
      const localY = (annotation.position.y - cy) * z + cy + viewTransform.panY;
      fontSize = annotation.fontSize * z;
      // Native input is rendered with extra height (fontSize * 1.4) and `textAlignVertical: 'center'`
      // so glyphs aren't clipped — shift the top up by half the extra padding so the centred glyphs
      // land at the SVG baseline instead of below it.
      top = localY - fontSize * ASCENT_RATIO - fontSize * 0.2;
    }
  }

  const onFocus = () => {
    hasFocusedRef.current = true;
  };

  const onBlur = () => {
    if (hasFocusedRef.current) onCommit();
  };

  const onKeyPress = isWeb
    ? (e: { nativeEvent?: { key?: string }; preventDefault?: () => void }) => {
        if (e?.nativeEvent?.key === 'Escape') {
          e.preventDefault?.();
          onCancel();
        }
      }
    : undefined;

  return {
    position: { left, top, fontSize },
    onFocus,
    onBlur,
    onKeyPress,
  };
};
